import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";

import {
  Product,
  ResponseOfProduct,
  ListResponseOfProduct,
  AdminListRequestOfProductFilterV2,
  ProductFilterV2,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";

type SelectProductProps = {
  organizationId?: string;
  value?: string | null;
  onChange: (id: string | null) => void;
  searchByName?: boolean;
  requireOrganizationId?: boolean;
  changeProductCallback?: (product: Product | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<Product>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectProduct = ({
  organizationId,
  value: productId,
  onChange,
  changeProductCallback,
  searchByName = false,
  requireOrganizationId = false,
  ...rest
}: SelectProductProps) => {
  const { formatMessage: t } = useIntl();
  const { productService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [product, setProduct] = useState<Product | null>(null);

  // Load the initial value if we're passed a productId
  const { isLoading, data } = useQuery<ResponseOfProduct>(
    ["product", productId],
    () => productService.getProductById(productId!),
    {
      enabled: !!productId && (!product || product.id !== productId),
    }
  );

  useEffect(() => {
    // Once the initial value loads, update the value (if is hasn't been set by a search)
    if (data?.data) {
      setProduct(data?.data!);
      changeProductCallback && changeProductCallback(data?.data!);
    }
  }, [data, product, changeProductCallback]);

  const fetchProducts = async (searchTerm: string) => {
    if (requireOrganizationId && !organizationId) {
      return {
        result: { data: [] as Product[] } as ListResponseOfProduct,
        forSearchTerm: searchTerm,
      };
    }
    const result = await queryClient.fetchQuery<ListResponseOfProduct>(
      ["products", organizationId, searchTerm],
      () =>
        productService.getProductsV2(
          AdminListRequestOfProductFilterV2.fromJS({
            organizationId,
            pageNumber: 0,
            pageSize: 20,
            sortField: "computedName",
            parameters: ProductFilterV2.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
              searchByName: searchByName,
            }),
          })
        )
    );

    return { result, forSearchTerm: searchTerm };
  };

  const onChangeProduct = (product: Product | null) => {
    setProduct(product);
    changeProductCallback && changeProductCallback(product);
    onChange(product?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={product}
      isLoadingInitialValue={isLoading}
      labelField="computedName"
      idField="id"
      fetchData={fetchProducts}
      failMessage={t({ id: "selectProductFetchFailed" })}
      onChange={onChangeProduct}
    />
  );
};

export default SelectProduct;
